import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Snackbar,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  deleteUser,
  signOut,
} from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase.js";

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function parseStoredLanguage(raw) {
  try { return raw ? JSON.parse(raw) : 0; }
  catch { return 0; }
}

function clearAuthStorage() {
  ['classCode', 'school', 'name', 'classes', 'language'].forEach(k =>
    localStorage.removeItem(k)
  );
}

function userDocRef(emailLower) {
  return doc(db, "peerTutoring", "users", "userDocs", emailLower);
}

function normalizeClassCode(raw) {
  return raw.trim().toLowerCase();
}

function normalizeTelegramUsername(raw) {
  return raw.trim().replace(/^@+/, '');
}

function rosterNameForEmail(students, emailLower) {
  if (!Array.isArray(students)) return null;
  const exact = students.find((obj) => obj && typeof obj === 'object' && emailLower in obj);
  if (exact) return exact[emailLower];
  const ci = students.find(
    (obj) =>
      obj &&
      typeof obj === 'object' &&
      Object.keys(obj).some((k) => k.toLowerCase() === emailLower)
  );
  if (ci) {
    const key = Object.keys(ci).find((k) => k.toLowerCase() === emailLower);
    if (key) return ci[key];
  }
  return null;
}

function classDocIdAttempts(d) {
  const raw = String(d.classCode ?? d.group ?? '').trim();
  if (!raw) return [];
  return [...new Set([normalizeClassCode(raw), raw, raw.toLowerCase(), raw.toUpperCase()])].filter(Boolean);
}

async function loadClassDocForSchool(school, d) {
  const raw = String(d.classCode ?? d.group ?? '').trim();
  const attempts = classDocIdAttempts(d);
  if (!attempts.length) return { classData: null, classDocId: '' };
  for (const id of attempts) {
    const snap = await getDoc(doc(db, school, id));
    if (snap.exists()) return { classData: snap.data(), classDocId: snap.id };
  }
  return { classData: null, classDocId: raw ? normalizeClassCode(raw) : '' };
}

async function resolveSessionFromUserDoc(emailLower) {
  const snap = await getDoc(userDocRef(emailLower));
  if (!snap.exists()) {
    throw Object.assign(new Error('NO_USER_DOC'), {
      uiMessage: 'Հաշիվը դեռ չի ակտիվացվել։ Դիմեք ադմինիստրատորին։',
    });
  }
  const d = snap.data();
  const school = d.school;
  if (!school) {
    throw Object.assign(new Error('INCOMPLETE_USER_DOC'), {
      uiMessage: 'Օգտվողի տվյալները անլրացուց են։',
    });
  }

  const { classData, classDocId } = await loadClassDocForSchool(school, d);
  if (!classDocId) {
    throw Object.assign(new Error('INCOMPLETE_USER_DOC'), {
      uiMessage: 'Օգտվողի տվյալները անլրացուց են։',
    });
  }

  let classes = typeof d.classes === 'number' ? d.classes : null;
  let rosterName = null;
  if (classData) {
    if (classes == null) {
      const n = classData.classes;
      classes = typeof n === 'number' ? n : 8;
    }
    rosterName = rosterNameForEmail(classData.students ?? [], emailLower);
  }
  if (classes == null) classes = 8;

  const combined = [d.firstName, d.lastName].filter(Boolean).join(' ').trim();
  const docName = String(d.name ?? combined ?? d.firstName ?? '').trim();
  const name = rosterName || docName || emailLower;
  return { school, classCode: classDocId, name, classes };
}

async function assertSignupMatchesUserDoc(emailLower, school, classCodeNorm) {
  const snap = await getDoc(userDocRef(emailLower));
  if (!snap.exists()) {
    throw Object.assign(new Error('NO_USER_DOC'), {
      uiMessage: 'Գրանցումը հասանելի չէ․ հաշիվը դեռ չի ակտիվացվել։',
    });
  }
  const d = snap.data();
  
  const docSchool = d.school;
  const docClass = normalizeClassCode(String(d.classCode ?? d.group ?? ''));
  if (docSchool !== school || docClass !== classCodeNorm) {
    throw Object.assign(new Error('USER_DOC_MISMATCH'), {
      uiMessage: 'Դպրոցը կամ դասարանը չեն համընկնում ձեր հաստատման հետ։',
    });
  }
}

const AUTH_ERROR_MESSAGES = {
  'auth/invalid-login-credentials': 'Սխալ էլ․ հասցե կամ գաղտնաբառ',
  'auth/wrong-password':            'Սխալ էլ․ հասցե կամ գաղտնաբառ',
  'auth/user-not-found':            'Սխալ էլ․ հասցե կամ գաղտնաբառ',
  'auth/email-already-in-use':      'Այս էլ․ հասցեն արդեն գրանցված է',
  'auth/weak-password':             'Գաղտնաբառը պետք է լինի առնվազն 6 նիշ',
  'auth/invalid-email':             'Անվավեր էլ․ հասցե',
  'auth/too-many-requests':         'Չափից շատ փորձ։ Փորձեք ավելի ուշ',
};

export default function LoginPage({
  setPermission, onLogin,
  classCode, setClassCode,
  school,     setSchool,
  setName, setClasses, setLanguage,
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [telegramUsername, setTelegramUsername] = useState('');
  const [mode, setMode] = useState('signin');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading,  setLoading]  = useState(true);   // initial session check
  const [busy,     setBusy]     = useState(false);  // form submitting

  const showError = (message) => setSnackbar({ open: true, message, severity: 'error' });

  const commitSession = useCallback(({ name, classes, language, school: s, classCode: cc }) => {
    setSchool(s);
    setClassCode(cc);
    setName(name);
    setClasses(classes);
    setLanguage(language);
    setPermission('write');
    localStorage.setItem('school', s);
    localStorage.setItem('classCode', cc);
    localStorage.setItem('name', name);
    localStorage.setItem('classes', String(classes));
    localStorage.setItem('language', JSON.stringify(language));
    onLogin();
  }, [onLogin, setClassCode, setClasses, setLanguage, setName, setPermission, setSchool]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const emailLower = user.email.toLowerCase();
        const session = await resolveSessionFromUserDoc(emailLower);
        const language = parseStoredLanguage(localStorage.getItem('language'));
        setLoading(false);
        commitSession({
          name: session.name,
          classes: session.classes,
          language,
          school: session.school,
          classCode: session.classCode,
        });
      } catch (err) {
        console.warn('Auto-login failed:', err.message);
        await signOut(auth).catch(() => {});
        clearAuthStorage();
        showError(err.uiMessage ?? 'Չհաջողվեց ավտոմատ մուտք։ Խնդրում ենք մուտք գործել կրկին');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [commitSession]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validEmail = email.trim().toLowerCase();

    if (!validEmail) return showError('Լրացրեք էլ․ հասցեն');
    if (!isValidEmail(validEmail)) return showError('Անվավեր էլ․ հասցե');
    if (!password) return showError('Լրացրեք գաղտնաբառը');
    if (password.length < 6) return showError('Գաղտնաբառը պետք է լինի առնվազն 6 նիշ');

    if (mode === 'signup') {
      if (!school) return showError('Ընտրեք դպրոց');
      const cc = classCode.trim();
      if (!cc) return showError('Լրացրեք դասարանի կոդը');
    }

    setBusy(true);

    let createdUser = null;
    try {
      if (mode === 'signin') {
        await signInWithEmailAndPassword(auth, validEmail, password);
        const session = await resolveSessionFromUserDoc(validEmail);
        const language = parseStoredLanguage(localStorage.getItem('language'));
        commitSession({
          name: session.name,
          classes: session.classes,
          language,
          school: session.school,
          classCode: session.classCode,
        });
      } else {
        const activeSchool = school;
        const activeClass = normalizeClassCode(classCode);
        await assertSignupMatchesUserDoc(validEmail, activeSchool, activeClass);

        const cred = await createUserWithEmailAndPassword(auth, validEmail, password);
        createdUser = cred.user;

        const tg = 0 || normalizeTelegramUsername(telegramUsername);
        if (tg != 0) {
          await updateDoc(userDocRef(validEmail), {
            username: tg,
            email: validEmail,
          });
        } else {
          await updateDoc(userDocRef(validEmail), {
            email: validEmail,
          });
        }

        const session = await resolveSessionFromUserDoc(validEmail);
        const language = 0;
        commitSession({
          name: session.name,
          classes: session.classes,
          language,
          school: session.school,
          classCode: session.classCode,
        });
      }
    } catch (err) {
      if (createdUser) {
        await deleteUser(createdUser).catch(() => {});
      }

      const msg = err.uiMessage ?? AUTH_ERROR_MESSAGES[err.code] ?? 'Մուտքը ձախողվեց';
      showError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#26b8b8' }} />
      </Box>
    );
  }

  return (
    <Paper elevation={4} sx={{ p: 3, maxWidth: 400, mx: 'auto', borderRadius: 3, mt: 8 }}>
      <Typography variant="h5" mb={2}>{mode === 'signin' ? 'Մուտք' : 'Գրանցում'}</Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        {mode === 'signup' && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel>Դպրոց</InputLabel>
              <Select value={school} label="Դպրոց" onChange={(e) => setSchool(e.target.value)}>
                <MenuItem value="shirakatsy">Shirakatsy Lyceum</MenuItem>
                <MenuItem value="164">164</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Դասարան"
              value={classCode}
              onChange={(e) => setClassCode(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="օր. 10b"
            />
          </>
        )}
        <TextField label="Էլ․ հասցե" type="email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth margin="normal" />
        <TextField label="Գաղտնաբառ" type="password" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth margin="normal" />
        {mode === 'signup' && (
          <TextField
            label="Telegram օգտանուն"
            value={telegramUsername}
            onChange={(e) => setTelegramUsername(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="@username կամ username"
            helperText="Պահպանվում է ձեր peer tutoring պրոֆիլում"
          />
        )}
        <Button type="submit" variant="contained" fullWidth disabled={busy} sx={{ mt: 3, backgroundColor: '#26b8b8', '&:hover': { backgroundColor: '#1ea0a0' } }}>
          {busy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : (mode === 'signin' ? 'Մուտք' : 'Գրանցում')}
        </Button>
      </Box>
      <Box mt={1}>
        <Button variant="text" onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); }}>
          {mode === 'signin' ? 'Գրանցվել' : 'Մուտք'}
        </Button>
      </Box>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Paper>
  );
}
